// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { type LoaderFunction } from 'react-router';
import { trackPromise } from 'react-promise-tracker';
import { RTK_PROMISE_TRACKER_ID } from '@shared/data-access/utils/constants';
import { appStore } from '@advisor-app/store/store';
import {
  resetRebalanceConfig,
  updateOptimizationConfig,
} from '@advisor-app/store/slices/parameters';
import {
  selectCurrentPortfolio,
  selectTargetPortfolio,
  portfolioPositionsSlice,
} from '@shared/data-access/slices/portfolio-positions';
import { setWorkflow } from '@advisor-app/store/slices/workflow';
import {
  type TypeWorkflow,
  type WorkflowAsset,
} from '@shared/data-access/interfaces/redux/workflow-state';
import { assetsApi } from '@shared/data-access/api/ai/assets-api';
import { getAssetsInfo } from '@shared/data-access/transformers/assets/get-assets-info';

export const workflowLoader =
  (type: TypeWorkflow = '', asset: WorkflowAsset = ''): LoaderFunction =>
  async ({ request }) =>
    trackPromise(
      (async () => {
        const url = new URL(request.url);
        const params = url.searchParams;

        if (type !== 'load') appStore.dispatch(resetRebalanceConfig());

        const workflows = ['load', 'rebalance', 'analysis', 'tax-harvesting'];

        if (
          !workflows.includes(type) &&
          !workflows.includes(params.get('workflow') as string)
        ) {
          appStore.dispatch(portfolioPositionsSlice.actions.reset());
        }

        appStore.dispatch(setWorkflow({ asset, type }));

        if (url.pathname.includes('ai-builder'))
          appStore.dispatch(
            updateOptimizationConfig([
              'ai-builder',
              { benchmark: [{ ticker: 'SPY', weight: 1 }] },
            ]),
          );

        return { ok: true };
      })(),
      RTK_PROMISE_TRACKER_ID,
    );

export const rebalanceConfigurationLoader: LoaderFunction = () =>
  trackPromise(
    (async () => {
      const currentPortfolio = selectCurrentPortfolio(appStore.getState());
      const targetPortfolio = selectTargetPortfolio(appStore.getState());

      // Ensure currentPortfolio has asset details
      if (currentPortfolio.length > 0) {
        const tickers = currentPortfolio.map(({ ticker }) => ticker);
        const assetsInfo = await getAssetsInfo(tickers, appStore.dispatch);

        const detailedCurrentPortfolio = currentPortfolio.map(position => {
          const assetInfo =
            position.details ??
            assetsInfo.find(
              asset =>
                asset.ticker === position.ticker || asset.isin === position.ticker,
            );

          return { ...position, details: assetInfo };
        });

        appStore.dispatch(
          portfolioPositionsSlice.actions.setCurrentPortfolioPositions(
            detailedCurrentPortfolio,
          ),
        );

        // Update targetPortfolio with asset details if it exists
        if (targetPortfolio.length > 0) {
          const detailedTargetPortfolio = targetPortfolio.map(targetAsset => {
            const assetInfo =
              targetAsset.details ??
              assetsInfo.find(
                asset =>
                  asset.ticker === targetAsset.ticker ||
                  asset.isin === targetAsset.ticker,
              );

            return {
              ...targetAsset,
              details: assetInfo,
              isin: assetInfo?.isin ?? targetAsset.isin,
              name: assetInfo?.name ?? targetAsset.name,
            };
          });

          appStore.dispatch(
            portfolioPositionsSlice.actions.setTargetPortfolioPositions(
              detailedTargetPortfolio,
            ),
          );
        }
      }

      return { ok: true };
    })(),
    RTK_PROMISE_TRACKER_ID,
  );

export const portfolioPositionsLoader =
  (assetType: string): LoaderFunction =>
  () =>
    trackPromise(
      (async () => {
        const currentPortfolio = selectCurrentPortfolio(appStore.getState());
        const targetPortfolio = selectTargetPortfolio(appStore.getState());

        const isins = currentPortfolio
          .map(({ ticker }) => ticker)
          .filter(ticker => ticker.length >= 12);

        if (isins.length === 0) return { ok: true };

        const assetsTypes: Record<string, string> = {
          stock: 'stocks',
          etf: 'etfs',
          mutual_fund: 'mutual_funds',
        };

        const searchResults = await Promise.all(
          isins.map(isin =>
            appStore
              .dispatch(
                assetsApi.endpoints.search.initiate({
                  ticker: isin,
                  name: isin,
                  assetType: assetsTypes[assetType],
                  key_search: isin,
                  isin,
                  details: false,
                }),
              )
              .unwrap(),
          ),
        );

        const assetsResults = new Map(
          searchResults.flat().flatMap(value => [
            [value.isin, value],
            [value.ticker, value],
          ]),
        );

        appStore.dispatch(
          portfolioPositionsSlice.actions.setCurrentPortfolioPositions(
            currentPortfolio.map(position => ({
              ...position,
              ticker: assetsResults.get(position.ticker)?.ticker ?? position.ticker,
            })),
          ),
        );

        appStore.dispatch(
          portfolioPositionsSlice.actions.setTargetPortfolioPositions(
            targetPortfolio.map(position => ({
              ...position,
              ticker: assetsResults.get(position.ticker)?.ticker ?? position.ticker,
            })),
          ),
        );

        return { ok: true };
      })(),
      RTK_PROMISE_TRACKER_ID,
    );
